'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { getEntryData } from '@/logic/reward-machine/entry-data';
import { useStore } from '@/store/store';
import { getServerTime } from '@/logic/aggregation/server-time';
import type { EntryData } from '@/types/reward-machine';

const ENTRY_THROTTLE_MS = 5000;

type ServerTime = { loading: boolean; time?: string };

export const useRewardMachineEntry = ({
    providedServerTime,
    loggedIn,
    timezone,
    initialEntryData
}: {
    providedServerTime?: ServerTime;
    loggedIn: boolean;
    timezone?: string;
    initialEntryData?: EntryData | null;
}) => {
    const {
        setEntryData,
        entry: entryData,
        entryFetchInFlight,
        entryLastSuccessAt,
        setEntryFetchInFlight,
        setEntryLastSuccessAt
    } = useStore((state) => state.rewardMachine);
    const [serverTime, setServerTime] = useState<ServerTime | undefined>(providedServerTime);
    const refreshingTimeRef = useRef(false);
    const forceRefetchRef = useRef(false);

    useEffect(() => {
        if (initialEntryData && !entryData) {
            setEntryData(initialEntryData);
            if (providedServerTime?.time) {
                setEntryLastSuccessAt(new Date(providedServerTime.time).getTime());
            }
        }
    }, [initialEntryData, entryData, setEntryData, providedServerTime, setEntryLastSuccessAt]);

    const fetchEntryData = useCallback(() => {
        const { time, loading } = serverTime ?? {};

        if (!loggedIn || !time || loading || entryFetchInFlight) return;

        const serverTimeMs = new Date(time).getTime();

        const forceRefetch = forceRefetchRef.current;
        if (!forceRefetch && serverTimeMs - entryLastSuccessAt < ENTRY_THROTTLE_MS) return;
        forceRefetchRef.current = false;

        setEntryFetchInFlight(true);
        getEntryData(time)
            .then((data) => {
                setEntryData({ error: data?.error, ...data });
            })
            .catch((error: Error) => {
                console.error('Rewards Machine Entry call failed:', error);
                setEntryData({ error } as EntryData);
            })
            .finally(() => {
                setEntryLastSuccessAt(serverTimeMs);
                setEntryFetchInFlight(false);
            });
    }, [
        loggedIn,
        serverTime,
        setEntryData,
        entryFetchInFlight,
        entryLastSuccessAt,
        setEntryFetchInFlight,
        setEntryLastSuccessAt
    ]);

    const triggerEntryCall = useCallback(async () => {
        if (refreshingTimeRef.current) return;
        if (!timezone) {
            console.warn('Timezone is not provided for Rewards Machine Entry call.');
            return;
        }

        refreshingTimeRef.current = true;
        try {
            const time = await getServerTime(timezone);
            forceRefetchRef.current = true;
            setServerTime({ loading: false, time });
        } finally {
            refreshingTimeRef.current = false;
        }
    }, [timezone]);

    useEffect(() => {
        if (initialEntryData) return;
        fetchEntryData();
    }, [fetchEntryData, initialEntryData]);

    return { entryData, fetchEntryData, triggerEntryCall };
};
